/**
 * useStoredPhotoUrl — an object URL for a sheet photo kept on this device
 * (`lib/chefs/temp-log-photos.ts`), loaded while `enabled` and revoked when it
 * is turned off, the photo changes or the owner unmounts.
 *
 * Shared by the scan card's full-image dialog (`SheetScanCard`, loaded when the
 * dialog opens) and the chef's phone Orders sheet card (loaded at once, for its
 * thumbnail). `missing` is true when the bytes are gone from this device.
 */

import { useEffect, useState } from 'react'

export interface StoredPhotoUrl {
  url: string | null
  missing: boolean
}

const NONE: StoredPhotoUrl = { url: null, missing: false }

export function useStoredPhotoUrl(
  photoId: string | null,
  loadPhotoUrl: ((photoId: string) => Promise<string | null>) | undefined,
  enabled: boolean,
): StoredPhotoUrl {
  const [state, setState] = useState<StoredPhotoUrl>(NONE)
  useEffect(() => {
    if (!enabled || !photoId || !loadPhotoUrl) {
      return
    }
    let loaded: string | null = null
    let cancelled = false
    void loadPhotoUrl(photoId).then((url) => {
      if (cancelled) {
        if (url) {
          URL.revokeObjectURL(url)
        }
        return
      }
      loaded = url
      setState({ url, missing: url === null })
    })
    return () => {
      cancelled = true
      setState(NONE)
      if (loaded) {
        URL.revokeObjectURL(loaded)
      }
    }
  }, [enabled, photoId, loadPhotoUrl])
  return state
}
